import React from "react";
import { useTranslations } from "next-intl";
import {
  ArIcon,
  Facebook,
  GlobeIcon,
  Instagram,
  Kuwait,
  Linkedin,
  Snapchat,
  UnitedArabEmirates
} from "@/components/icons";
import Twitter from "@/components/icons/social/Twitter";
import { Divider } from "@mui/material";
import { getLocalizationByCountry } from "@/helpers/getLocalizationByCountry";
import { useRouter } from "next/router";
import { useLocalization } from "@/context/LocalizationContext";
import { useUserJourney } from "@/hooks/useUserJourney";

const Footer = () => {
  const t = useTranslations("Footer");
  const currentYear = new Date().getFullYear();
  const { trackEvent, getFullUrl } = useUserJourney("1231231211");

  const socialMediaLinks = [
    { icon: <Facebook />, name: t("facebook") },
    { icon: <Linkedin />, name: t("linkedin") },
    { icon: <Instagram />, name: t("instagram") },
    { icon: <Twitter />, name: t("twitter") },
    // { icon: <Youtube />, name: t("youtube") },
    { icon: <Snapchat />, name: t("snapchat") },
  ];
  const countries = [
    { flag: <ArIcon />, name: t("saudi"), code: "SA" },
    { flag: <Kuwait />, name: t("kuwait"), code: "KW" },
    { flag: <UnitedArabEmirates />, name: t("uae"), code: "AE" },
    { icon: <GlobeIcon />, name: t("worldwide"), code: "US" },
  ];
  const footerSections = [
    { title: t("corporate"), links: ["about", "careers"] },
    { title: t("legal"), links: ["terms-and-conditions", "privacy-policy"] },
    { title: t("support"), links: ["contact-us", "faqs"] },
  ];
  const businessInfo = [
    { title: t("commercialTitle"), value: t("commercialValue") },
  ];

  const paymentMethods = [
    "/images/payment/visa.png",
    "/images/payment/master.png",
    "/images/payment/amex.png",
    "/images/payment/applepay.png",
    "/images/payment/KuwaitIco.png"
    // "/images/payment/mada.png",
    // "/images/payment/mokafa.png",
    // "/images/payment/qitaf.png",
  ];

  const router = useRouter();
  const { setLocalization } = useLocalization();
  const handleCountryClick = async (countryCode: string) => {
    const localization = getLocalizationByCountry(countryCode);
    setLocalization({
      lang: localization.lang as "ar" | "en",
      currency: localization.currency,
      nationality: localization.nationality,
    });

    trackEvent({
      page: router.pathname.replace("/", ""),
      url: getFullUrl(),
      action: "page-load",
      api_payload: { device: "desktop", countryCode },
      api_response: localization,
      api_error: false,
    });

    const newLocale = localization.lang;
    const pathWithoutLocale = router.asPath.replace(/^\/(ar|en)/, "");
    const newPath = `/${newLocale}${pathWithoutLocale}`;

    if (router.locale !== localization.lang) {
      // let newPath = router.asPath;
      await router.replace(newPath, undefined, { locale: newLocale });
    }
    window.scrollTo(0, 0);
  };

  return (
    <footer className="w-full  py-6 bg-primary text-white">
      <div className="flex flex-col gap-4 max-w-[1312px] mx-auto">
        <div className="w-full flex flex-wrap px-4 py-0">
        <div className="flex flex-wrap max-xxs:flex-col w-full gap-5 max-xxs:gap-8 pb-4">
          <div className="w-full  flex flex-col items-start md:!w-[160px] c-lg:h-full mb-4">
            <img
              className="object-contain  w-[100px]  h-[120px]"
              alt="almuayed logo"
              src="/images/logo-v2.png"
            />
          </div>

          {footerSections.map((section, index) => (
            <div
              key={`section-${index}`}
              className="w-full  sm:w-[calc(50%-1.25rem)] md:w-[calc(33.333%-1.25rem)] lg:w-[calc(20%-1.25rem)] !min-w-[154px] flex-1"
            >
              <h3 className="font-semibold text-lg leading-7 xxs:mb-6 mb-4">
                {section.title}
              </h3>
              <ul className="xxs:space-y-4 max-xxs:flex max-xxs:flex-row max-xxs:gap-8 max-xxs:items-center max-xxs:flex-wrap">
                {section.links.map((link, linkIndex) => (
                  <li key={`link-${linkIndex}`} className="max-xxs:w-[45%]">
                    <a
                      href={link}
                      className="font-medium text-base leading-5 text-nowrap"
                    >
                      {t(link)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="w-full sm:w-[calc(50%-1.25rem)] md:w-[calc(33.333%-1.25rem)] lg:w-[calc(20%-1.25rem)] min-w-[154px] flex-1">
            <h3 className="font-semibold text-lg leading-7 xxs:mb-6 mb-4">
              {t("socialMedia")}
            </h3>
            <ul className="xxs:space-y-4 max-xxs:flex max-xxs:flex-row max-xxs:gap-y-6 max-xxs:gap-x-8 max-xxs:items-center max-xxs:flex-wrap">
              {socialMediaLinks.map((social, index) => (
                <li
                  key={`social-${index}`}
                  className="flex items-center gap-4 max-xxs:w-[45%]"
                >
                  {social.icon}
                  <a href="#" className="font-medium text-base leading-5">
                    {social.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="w-full sm:w-[calc(50%-1.25rem)] md:w-[calc(33.333%-1.25rem)] lg:w-[calc(20%-1.25rem)] min-w-[154px] flex-1">
            <h3 className="font-semibold text-lg leading-7 xxs:mb-6 mb-4">
              {t("countries")}
            </h3>
            <ul className="xxs:space-y-4 max-xxs:flex max-xxs:flex-row max-xxs:gap-y-6 max-xxs:gap-x-8 max-xxs:items-center max-xxs:flex-wrap">
              {countries.map((country, index) => (
                <li
                  key={`country-${index}`}
                  className="flex items-center gap-4 max-xxs:min-w-[45%] max-xxs:w-fit cursor-pointer"
                  onClick={() => handleCountryClick(country.code)

                  }
                >
                  {country.icon ? (
                    country.icon
                  ) : (
                    <span className="me-2">{country.flag}</span>
                  )}
                  <span className="font-medium text-base leading-5">
                    {country.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="w-full px-4 flex flex-col items-center">
        <div className="flex justify-center gap-2 mb-4">
          {paymentMethods.map((src, index) => (
            <img
              key={index}
              src={src}
              alt="Payment method"
              className="h-8 w-8 object-contain rounded border bg-white"
            />
          ))}
        </div>

        <div className="text-xs  py-4">
          {t("copyright", { year: currentYear })}
        </div>
        <div className="flex w-full justify-between mt-5 text-center">
          {businessInfo.map((info, index) => (
            <div
              key={`info-${index}`}
              className="flex-1 flex flex-col items-center"
            >
              <div className="text-[11px]  font-semibold mb-1">
                {info.title}
              </div>
              <div className="text-xs ">{info.value}</div>
              {index < businessInfo.length - 1 && (
                <Divider
                  orientation="vertical"
                  className="absolute h-[25px] mx-auto  bg-[#d8dcde]"
                  flexItem
                />
              )}
            </div>
          ))}
        </div>
      </div>
      </div>
    </footer>
  );
};

export default Footer;
